import { useEffect, useState } from "react";
import { MessageSquare, History, CheckCircle2, Circle, AtSign, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { fmtDateTime } from "../lib/format";

const ts = (v) => fmtDateTime(v);

// Comments + automatic who-changed-what trail for the shared Run Payroll draft.
export function DraftActivity({ companyId, comments = [], history = [], onUpdated, me }) {
  const [tab, setTab] = useState("comments");
  const [open, setOpen] = useState(true);
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const unresolved = comments.filter((c) => !c.resolved).length;

  useEffect(() => {
    if (!companyId) return;
    api.get(`/payroll/draft/mentionable?company_id=${companyId}`).then((r) => setPeople(r.data.users || [])).catch(() => setPeople([]));
  }, [companyId]);

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/payroll/draft/comments", { company_id: companyId, text: text.trim(), mentions });
      setText(""); setMentions([]); onUpdated(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const resolve = async (c) => {
    try { const r = await api.post(`/payroll/draft/comments/${c.comment_id}/resolve`, { company_id: companyId, resolved: !c.resolved }); onUpdated(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const nameOf = (email) => people.find((p) => p.email === email)?.name || email;

  return (
    <div className="border border-border bg-white" data-testid="draft-activity">
      <button data-testid="draft-activity-toggle" onClick={() => setOpen((o) => !o)} className="w-full px-4 py-2.5 flex items-center gap-2 text-left hover:bg-neutral-50 transition-colors">
        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Draft activity</span>
        {unresolved > 0 && <span data-testid="draft-unresolved-badge" className="text-[9px] font-mono bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5">{unresolved} open</span>}
        <span className="ml-auto text-[10px] font-mono text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="border-t border-border">
          <div className="flex text-[10px] font-mono uppercase tracking-widest">
            <button data-testid="draft-tab-comments" onClick={() => setTab("comments")} className={`flex-1 px-3 py-2 inline-flex items-center justify-center gap-1.5 border-b-2 ${tab === "comments" ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}><MessageSquare className="h-3 w-3" /> Comments ({comments.length})</button>
            <button data-testid="draft-tab-changes" onClick={() => setTab("changes")} className={`flex-1 px-3 py-2 inline-flex items-center justify-center gap-1.5 border-b-2 ${tab === "changes" ? "border-black text-black" : "border-transparent text-muted-foreground hover:text-black"}`}><History className="h-3 w-3" /> Changes ({history.length})</button>
          </div>
          {tab === "comments" ? (
            <div>
              <div className="p-3 border-b border-border space-y-2">
                <textarea data-testid="draft-comment-input" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Leave a note for whoever finalizes this run…" className="w-full border border-input px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-black resize-none" />
                <div className="flex flex-wrap items-center gap-1.5">
                  <select data-testid="draft-comment-mention-select" value="" onChange={(e) => { const v = e.target.value; if (v && !mentions.includes(v)) setMentions([...mentions, v]); }} className="border border-input px-1.5 py-1 text-[10px] font-mono focus:outline-none">
                    <option value="">@ Tag colleague…</option>
                    {people.filter((p) => p.email !== me && !mentions.includes(p.email)).map((p) => <option key={p.email} value={p.email}>{p.name}{p.staff ? " · staff" : ""}</option>)}
                  </select>
                  {mentions.map((m) => <span key={m} data-testid={`draft-comment-mention-${m}`} className="inline-flex items-center gap-1 text-[9px] font-mono border border-black px-1.5 py-0.5"><AtSign className="h-2.5 w-2.5" />{nameOf(m)}<button onClick={() => setMentions(mentions.filter((x) => x !== m))} aria-label="remove"><X className="h-2.5 w-2.5" /></button></span>)}
                  <button data-testid="draft-comment-submit" disabled={busy || !text.trim()} onClick={post} className="ml-auto h-7 px-3 bg-black text-white text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-800 transition-colors disabled:opacity-50">Post</button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto divide-y divide-border/60">
                {comments.length ? [...comments].reverse().map((c) => (
                  <div key={c.comment_id} data-testid={`draft-comment-${c.comment_id}`} className={`px-3 py-2 text-xs ${c.resolved ? "opacity-60" : ""}`}>
                    <div className="flex items-start gap-2">
                      <button data-testid={`draft-comment-resolve-${c.comment_id}`} onClick={() => resolve(c)} title={c.resolved ? "Re-open" : "Resolve"} className="mt-0.5 text-muted-foreground hover:text-black">{c.resolved ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700" /> : <Circle className="h-3.5 w-3.5" />}</button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2"><span className="font-medium">{c.by_name}</span><span className="font-mono tabular-nums text-[10px] text-muted-foreground">{ts(c.at)}</span></div>
                        <div className={`mt-0.5 whitespace-pre-wrap break-words ${c.resolved ? "line-through" : ""}`}>{c.text}</div>
                        {c.mentions?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{c.mentions.map((m) => <span key={m} className="text-[9px] font-mono border border-border px-1 py-0.5">@{nameOf(m)}</span>)}</div>}
                      </div>
                    </div>
                  </div>
                )) : <div className="px-3 py-5 text-xs text-muted-foreground text-center">No comments yet.</div>}
              </div>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-border/60">
              {history.length ? [...history].reverse().map((h, i) => (
                <div key={i} data-testid="draft-history-row" className="px-3 py-1.5 text-xs">
                  <div className="flex items-center justify-between gap-2 text-[10px] font-mono text-muted-foreground"><span>{h.by_name}</span><span className="tabular-nums">{ts(h.at)}</span></div>
                  <div>{h.text}</div>
                </div>
              )) : <div className="px-3 py-5 text-xs text-muted-foreground text-center">No changes recorded yet.</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
